import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type {
  CacheDeleteParams,
  CacheDeleteResult,
  CacheEntryDetail,
  CacheListResult,
  CacheStatsResult,
} from '../types/cache';

type DefaultError = unknown;

export function useCacheEntries<TData = CacheListResult, TError = DefaultError>(
  searchParams: Record<string, any> = {},
  options?: Omit<
    UseQueryOptions<CacheListResult, TError, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<TData, TError> {
  const tenant =
    typeof searchParams.tenant === 'string' ? searchParams.tenant : undefined;
  const restaurantGuid =
    typeof searchParams.restaurantGuid === 'string'
      ? searchParams.restaurantGuid
      : undefined;
  const resource =
    typeof searchParams.resource === 'string'
      ? searchParams.resource
      : undefined;
  return useQuery<CacheListResult, TError, TData>({
    queryKey: [
      'cache',
      'entries',
      tenant ?? null,
      restaurantGuid ?? null,
      resource ?? null,
    ],
    queryFn: () =>
      service.getCacheEntries({ tenant, restaurantGuid, resource }),
    ...(options as any),
  });
}

export function useCacheStats<TData = CacheStatsResult, TError = DefaultError>(
  options?: Omit<
    UseQueryOptions<CacheStatsResult, TError, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<TData, TError> {
  return useQuery<CacheStatsResult, TError, TData>({
    queryKey: ['cache', 'stats'],
    queryFn: () => service.getCacheStats(),
    ...(options as any),
  });
}

export function useCacheEntry<TData = CacheEntryDetail, TError = DefaultError>(
  key: string | null | undefined,
  options?: Omit<
    UseQueryOptions<CacheEntryDetail, TError, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<TData, TError> {
  return useQuery<CacheEntryDetail, TError, TData>({
    queryKey: ['cache', 'entry', key],
    queryFn: () => service.getCacheEntry(key as string),
    enabled: !!key,
    ...(options as any),
  });
}

export function useDeleteCacheEntries(
  options?: Omit<
    UseMutationOptions<CacheDeleteResult, DefaultError, CacheDeleteParams>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<CacheDeleteResult, DefaultError, CacheDeleteParams>({
    mutationFn: (params) => service.deleteCacheEntries(params),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['cache'] });
      options?.onSuccess?.(...args);
    },
  });
}
